import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useState } from "react";
import {
  DKLabel,
  DKInput,
  DKCheckMark,
  DKTooltipWrapper,
  DKIcon,
  DKExpandableWrapper,
  DKRadioButton,
  showAlert,
  DKLine
} from "deskera-ui-library";
import { fetchCPFAccount, selectCPFAccounts, selectEmployeeMeta } from "../../../Redux/Slices/EmployeeListSlice";
import { useDispatch, useSelector } from "react-redux";
import EmployeeListService from "../../../services/EmployeeListService";
import {
  COLUMN_CODES,
  COMPLIANCE_FIELDS_WITH_OR_CONDITIONAL_FIELDS,
  COMPLIANCE_SECTION,
  CONSTANTS,
  COUNTRY_RELIGION,
  ERF,
  FORMAT,
  INPUT_TYPE,
  MESSAGES_TOOLTIP,
} from "../../../constants/Constant";
import Utility, { deepClone, setReactRef, setTabIndex } from "../../../utility/Utility";
import {
    IC_NUMBER_REGEX,
  PAN_REGEX_PATTERN,
  PF_ACCOUNT_PATTERN_IMPORT,
  SSN_PATTERN,
} from "../../../constants/RegexConstants";
import RightPanel from "../RightPanel";
import DataGridUtility from "../../../utility/DataGridUtility";
import { TENANT, TenantService } from "../../../managers/TenantManagerService";
import ic_info_message from "../../../assets/icons/ic_info_gray01.png";
import { isUSCompliance, scrollToError } from "../../../managers/common/CommonUtil";
import moment from "moment";
import MaskInput from "../../../SharedComponents/MaskInput/MaskInput";
import MaskInputUtil from "../../../SharedComponents/MaskInput/MaskInputUtil";
import AddCpfAccount from "../../AddCpfAccount";
import { Nationalities } from "../../../constants/CountryConstant";
import ic_erro_info from "../../../assets/icons/ic_info_error.svg";
import EmployeeConfigService from "../../../services/EmployeeConfigService";
import EmployeeCustomSection from "./EmployeeCustomSection";
import CustomSectionManager from "../../../managers/CustomSectionManager";

const ComplianceDetails = forwardRef((props, ref) => {
  const isAdmin = TenantService.get(TENANT.IS_ADMIN);
  const userId = props.userId ? props.userId : "";
  const dispatch = useDispatch();
  const employeeMeta = useSelector(selectEmployeeMeta);
  const cpfAccountDetails = useSelector(selectCPFAccounts);
  const [employeeMetaConfig, setEmployeeMetaConfig] = useState(
    deepClone(employeeMeta)
  );
  const [complainceColumnConfig, setComplianceConfig] = useState([]);
  const [complianceDefaultValues, setDefaultValues] = useState({});
  const [employeeDetails, setEmployeeDetails] = useState(
    props.employeeDetailedData
  );
  const [employeeUpdatedInfo, setUpdatedInfo] = useState(props.employeeData);
  const [isUSCountry, setUSCountry] = useState(isUSCompliance());
  const [isInviteUserFlowEnabled, setInviteUserFlow] = useState(
    isAdmin && isUSCountry && employeeUpdatedInfo?.inviteUserToFillInfo
  );
  const [sectionExpandedState, setSectionExpandedState] = useState([]);

  const [mask, setMask] = useState(MaskInputUtil.MASK_STATE);
  const maskUnmaskObservable = MaskInputUtil.subscripeToMaskUnmaskObservable(setMask);
  const [showCPFPopup, enableCPFAccountPopup] = useState(false);

  const [customSections, setCustomSections] = useState(EmployeeConfigService.getNonDefaultFieldSections(3));
  const [customSectionFieldValues, setCustomSectionFieldValues] = useState([]);

  useEffect(() => {
    let complianceData =
      employeeDetails && employeeDetails.compliance
        ? employeeDetails.compliance
        : "";
    let configDetails = EmployeeListService.createComplainceColumnConfig(
      employeeMetaConfig,
      complianceDefaultValues,
      complianceData,
      isInviteUserFlowEnabled,
      sectionExpandedState,
      getExternalOptions(),
      // hasPermission
    );
    configDetails = deepClone(configDetails);
    validateEsiNumber(complianceDefaultValues.esiApplicable, configDetails);
    let congifs = hideShowColumnConfigForCompliance(configDetails, complianceDefaultValues);
    setComplianceConfig(congifs);

    const customSectionsToUpdate = CustomSectionManager.setCustomSectionFieldValues(customSections, employeeDetails["customSectionFields"]);
    const customSectionFieldValues = CustomSectionManager.getCustomSectionFields(customSectionsToUpdate);
    setCustomSectionFieldValues(customSectionFieldValues);

    return () => {
      MaskInputUtil.unsubscribe(maskUnmaskObservable);
    }
  }, [employeeDetails, cpfAccountDetails]);

  useEffect(() => {
    if (!isAdmin) {
        setTabIndex([ERF.ADD_EMPLOYEE]);
    }
  }, [complainceColumnConfig]);

  useEffect(() => {
    updatePropsValue();
  }, [complainceColumnConfig, customSectionFieldValues]);

  useEffect(() => {
    if (employeeUpdatedInfo) {
      setUpdatedInfo(employeeUpdatedInfo);
    }
  }, [employeeUpdatedInfo]);

  useImperativeHandle(ref, () => ({
    hightlightInvalidFieldsInCompliance() {
      let columns = deepClone(complainceColumnConfig);
      let isInvalid = false;
      const invalidFields = [];
      for (let i = 0; i < columns.length; i++) {
        let columnConfig = columns[i];
        let fields = columnConfig.fields;
        for (let item = 0; item < fields.length; item++) {
          let individualColumn = fields[item];
          const params = complianceDefaultValues;
          if (
            !fields.hidden &&
            individualColumn &&
            params &&
            ((individualColumn.uiVisible &&
              individualColumn.required &&
              Utility.isEmptyForCompliance(
                params[individualColumn.columnCode]
              )) ||
              individualColumn.hasError)
          ) {
            individualColumn.hasError = true;
            invalidFields.push(individualColumn.columnCode);
          }
        }
      }
      if(invalidFields?.length) {
        scrollToError(invalidFields[0]);
      }

      if (Array.isArray(customSections)) {
        const customSectionsToUpdate = CustomSectionManager.validateCustomSections(customSections);
        customSectionsToUpdate.forEach((section) => {
          if (Array.isArray(section.fields)) {
            section.fields.forEach((field) => {
              if (field.hasError) {
                invalidFields.push(field.id);
              }
            });
          }
        });

        setCustomSections(customSectionsToUpdate);

        if(invalidFields.length > 0) {
          scrollToError(invalidFields[0]);
        }
      }

      setComplianceConfig(columns);
    },
  }));

  const getExternalOptions = () => {
    return {
      "CPFAccounts" : cpfAccountDetails
    }
  }


  const updatePropsValue = () => {
    props.setData({
      columnConfig: {
        compliance: complainceColumnConfig,
        customSections: customSections
      },
      defaults: {
        ...complianceDefaultValues,
        customSectionFields: customSectionFieldValues
      },
    });
  };

  const onChange = (value, index, object, name, sectionIndex) => {
    if (value && value === " ") {
      value = value.trim();
    }
    let columnConfig = deepClone(complainceColumnConfig);
    switch (object.id) {
      case "panNumber":
        if (Utility.isEmpty(value)) {
          columnConfig[sectionIndex].fields[index].hasError = true;
        } else {
          const regexPAN = new RegExp(PAN_REGEX_PATTERN);
          columnConfig[sectionIndex].fields[index].hasError =
            !regexPAN.test(value);
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "uanNumber":
        if (!Utility.isEmpty(value)) {
          columnConfig[sectionIndex].fields[index].hasError =
            value.length != 12;
        } else {
          columnConfig[sectionIndex].fields[index].hasError = false;
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "pfAccountNumber":
        if (!Utility.isEmpty(value)) {
          const regexPAN = new RegExp(PF_ACCOUNT_PATTERN_IMPORT);
          columnConfig[sectionIndex].fields[index].hasError =
            !regexPAN.test(value);
        } else {
          columnConfig[sectionIndex].fields[index].hasError = false;
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "socialSecurityNumber":
        if (!Utility.isEmpty(value)) {
          const regexSSN = new RegExp(SSN_PATTERN);
          columnConfig[sectionIndex].fields[index].hasError =
            !regexSSN.test(value);
          if (columnConfig[sectionIndex].fields[index].hasError) {
            columnConfig[sectionIndex].fields[index].hasTooltip = true;
            columnConfig[sectionIndex].fields[index].tooltipText =
              MESSAGES_TOOLTIP.VALID_SSN;
          } else {
            columnConfig[sectionIndex].fields[index].hasTooltip = false;
          }
        } else {
          columnConfig[sectionIndex].fields[index].hasError = false;
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "esiNumber":
        if (Utility.isEmpty(value) && !complianceDefaultValues["esiApplicable"]) {
          columnConfig[sectionIndex].fields[index].hasError = false;
        } else {
          if (object.validators) {
            let { isInvalid, validationMsg } = setValidation(value, object);
            if (isInvalid) {
              columnConfig[sectionIndex].fields[index].hasError = isInvalid;
              columnConfig[sectionIndex].fields[index].errMsg = !Utility.isEmpty(validationMsg)
                ? validationMsg
                : CONSTANTS.EMPTY_STRING;
            } else {
              columnConfig[sectionIndex].fields[index].hasError = false;
              columnConfig[sectionIndex].fields[index].errMsg = CONSTANTS.EMPTY_STRING;
            }
          } else {
            if (Utility.isEmpty(value)) {
              columnConfig[sectionIndex].fields[index].hasError = true;
            } else {
              columnConfig[sectionIndex].fields[index].hasError = false;
            }
          }
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "icNumber":
        if (!Utility.isEmpty(value)) {
          const regexIC = new RegExp(IC_NUMBER_REGEX);
          columnConfig[sectionIndex].fields[index].hasError =
            !regexIC.test(value);
          if (columnConfig[sectionIndex].fields[index].hasError) {
            columnConfig[sectionIndex].fields[index].hasTooltip = true;
            columnConfig[sectionIndex].fields[index].tooltipText =
              "IC Number should of the format 123456-12-1234";
          } else {
            columnConfig[sectionIndex].fields[index].hasTooltip = false;
            columnConfig[sectionIndex].fields[index].tooltipText = CONSTANTS.EMPTY_STRING;
          }
        } else {
          columnConfig[sectionIndex].fields[index].hasError = false;
        }
        complianceDefaultValues[object.id] = value;
        setComplianceConfig(columnConfig);
        break;
      case "immigrationStatus":
        complianceDefaultValues[object.id] = value;
        if (!Utility.isEmptyObject(value)) {
            setSelfHelpGroupContributionValue(columnConfig);
            if (value?.id === 'foreignerEmploymentPass') {
                complianceDefaultValues["fwlApplicable"] = false;
            } else if (value?.id === 'foreignerSPass' || value?.id === 'workPermit') {
                complianceDefaultValues["fwlApplicable"] = true;
            } else {
                complianceDefaultValues["fwlApplicable"] = false;
            }
        if (value?.id === 'singaporeCitizen') {
                const nationality = Nationalities.find((n) => {
                    return n['id'] === 'singaporean';
                });
             if (!Utility.isEmptyObject(nationality)) {
                complianceDefaultValues["nationality"] = nationality;
                }
            }
        }
        break;
      case "race":
          complianceDefaultValues[object.id] = value;
          setSelfHelpGroupContributionValue(columnConfig);
          break;
      case "nationality":
          complianceDefaultValues[object.id] = value;
          setSelfHelpGroupContributionValue(columnConfig);
          break;
      case "religion":
          complianceDefaultValues[object.id] = value;
          setSelfHelpGroupContributionValue(columnConfig);
          break;
      case "startOfPermanentResident":
        complianceDefaultValues[object.id] = value;
        const employeeQualification = columnConfig[sectionIndex].fields.find((f) => f.id === "employeeQualification");
        if (employeeQualification) {
            complianceDefaultValues["employeeQualification"] = {};
            const currentDate = new Date();
            const twoYrsBackDate = new Date(currentDate.getFullYear() - 2, currentDate.getMonth(), 0);
            twoYrsBackDate.setHours(0, 0, 0, 0);
            if (value.getTime() <= twoYrsBackDate.getTime()) {
                employeeQualification.options = [
                    {
                        id: 'contributionNotAtFullRate',
                        name: 'Contribution Not At Full Rate'
                    }
                ];
            } else {
                employeeQualification.options = employeeQualification.defaultOptions;
            }
        }
        setComplianceConfig(columnConfig);
        break;
      case "assignCPFAccount":
        const selectedCpfAccount = EmployeeListService.getSelectedIdFromObject(value,"id");
        if(selectedCpfAccount === "clear_selection") {
          complianceDefaultValues[object.id] = {};
        } else { 
          complianceDefaultValues[object.id] = value;
        }
        setComplianceConfig(columnConfig);
        break;
      default:
        if (object.validators) {
          let { isInvalid, validationMsg } = setValidation(value, object);
          if (isInvalid) {
            columnConfig[sectionIndex].fields[index].hasError = isInvalid;
            columnConfig[sectionIndex].fields[index].errMsg = !Utility.isEmpty(
              validationMsg
            )
              ? validationMsg
              : CONSTANTS.EMPTY_STRING;
          } else {
            columnConfig[sectionIndex].fields[index].hasError = false;
            columnConfig[sectionIndex].fields[index].errMsg =
              CONSTANTS.EMPTY_STRING;
          }
          complianceDefaultValues[object.id] = value;
          setComplianceConfig(columnConfig);
        } else {
          if (Utility.isEmpty(value)) {
            columnConfig[sectionIndex].fields[index].hasError = true;
          } else {
            columnConfig[sectionIndex].fields[index].hasError = false;
          }
          complianceDefaultValues[object.id] = value;
          setComplianceConfig(columnConfig);
        }
    }
    if (
        object.type === INPUT_TYPE.DROPDOWN ||
        object.type === INPUT_TYPE.SELECT || object.type === INPUT_TYPE.CHECKBOX || 
        object.type === INPUT_TYPE.TOGGLE
    ) {
        showAndHideFields(complianceDefaultValues);
    }
    updatePropsValue();
    props.setUserDataUpdated(true);
  };

  const setSelfHelpGroupContributionValue = (columnConfig) => {
    const selfHelpGroupList = [];
    const immigrationStatus = complianceDefaultValues["immigrationStatus"]?.id || CONSTANTS.EMPTY_STRING;
    const nationality = complianceDefaultValues["nationality"]?.id || CONSTANTS.EMPTY_STRING;
    const race = complianceDefaultValues["race"]?.id || CONSTANTS.EMPTY_STRING;
    const religion = complianceDefaultValues["religion"]?.id || CONSTANTS.EMPTY_STRING;

    if ((immigrationStatus === 'permanentResident' || immigrationStatus === 'singaporeCitizen') && race === 'chinese') {
      selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.CDAC);
    }
    if ((immigrationStatus === 'permanentResident' || immigrationStatus === 'singaporeCitizen') && race === 'chinese') {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.CDAC)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.CDAC);
      }
    }
    if (race === 'indian') {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.SINDA)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.SINDA);
      }
    }
    if (race === 'eurasian') {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.ECF)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.ECF);
      }
    }
    if (nationality === 'BD' || nationality === 'BT' || nationality === 'IN' || nationality === 'MV' || nationality === 'NP' || nationality === 'PK' || nationality === 'LK') {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.SINDA)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.SINDA);
      }
    }
    if (religion === 'islam') {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.MBMF)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.MBMF);
      }
    }
    if (selfHelpGroupList.length == 0) {
      if (!isSelfHelpGroupValue(selfHelpGroupList, COUNTRY_RELIGION.selfGroup.NA)) {
        selfHelpGroupList.push(COUNTRY_RELIGION.selfGroup.NA);
      }
    }
    const selfHelpGroupOptions = getSelfHelpGroupOptions(columnConfig, "selfHelpGroupContribution");
    const selfHelpGroupValueIndexes = EmployeeListService.getValueFromMuliSelect(selfHelpGroupList, selfHelpGroupOptions, "id");
    complianceDefaultValues["selfHelpGroupContribution"] = selfHelpGroupValueIndexes;
}

  const isSelfHelpGroupValue = (groupList, value) => {
    if (groupList.indexOf(value) !== -1) {
      return true;
    } else {
      return false;
    }
  }

  const getSelfHelpGroupOptions = (columnConfig, fieldId) => {
      let options = [];
      if(columnConfig?.length) {
        for(let section = 0; section < columnConfig.length; section++) {
            if(columnConfig[section]?.fields) {
                const index = columnConfig[section].fields.findIndex(f=> f.id === fieldId);
                if(index != -1) {
                    const selfHelpField = columnConfig[section].fields[index];
                    options = selfHelpField.options;
                    break;
                }
            }
            
        }
      }
      return options;
  }

  const getValueFromDefaultValues = (value, fieldId, Columns) => {
      let filteredConfig = Columns.find((x) => x.id === fieldId);
      if(!Utility.isEmptyObject(filteredConfig)) {
        switch(filteredConfig.type) {
            case INPUT_TYPE.DROPDOWN:
                return value?.id;
            case INPUT_TYPE.CHECKBOX:
                 if(value && typeof(value) === "boolean") {
                    return value.toString();
                }
                return value
            default:
                return value;
        }
      }
  }

  const updateAdditonalValues = (field) => {
    const fieldId = field.fieldId || field.columnCode;
    switch(fieldId) {
        case "epfContribution":
            const residentStatus = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["residenceStatus"], "id");
            if(residentStatus  === "malaysian" || residentStatus  === "permanentresident") {
                if(!complianceDefaultValues["epfContribution"]) {
                    complianceDefaultValues["epfContribution"] = true;
                }
                field.readOnly = true;
            }
            break;
        case "sector":
            const immigrationStatus = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["immigrationStatus"], "id");

            if (immigrationStatus === 'foreignerSPass') {
                const sectorUpdatedOptions = [
                    {
                        id: 'services',
                        name: 'Services'
                    },
                    {
                        id: 'ALL_SECTORS_EXCL_SERVICES',
                        name: 'Others'
                    }
                ];
                field.options = sectorUpdatedOptions; 
                complianceDefaultValues["sector"] = !Utility.isEmptyObject(complianceDefaultValues["sector"]) ? EmployeeListService.getValueObjectFromDropdown(complianceDefaultValues["sector"].id, sectorUpdatedOptions, "id") : {};               
            }
            break;
        case "dependencyCeiling":
            const immigrationStatus1 = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["immigrationStatus"], "id");
            if (immigrationStatus1 === 'foreignerSPass') {
                const dependencyFieldUpdatedOptions = [
                    {
                        id: 'tier_1',
                        name: 'Base Tier / Tier 1'
                    },
                    {
                        id: 'tier-2',
                        name: 'Tier 2'
                    }
                ];
                field.options = dependencyFieldUpdatedOptions;
                complianceDefaultValues["dependencyCeiling"] = !Utility.isEmptyObject(complianceDefaultValues["dependencyCeiling"]) ? EmployeeListService.getValueObjectFromDropdown(complianceDefaultValues["dependencyCeiling"].id, dependencyFieldUpdatedOptions, "id") : {};
            }
            break;
        case "esiNumber":
            const isEsiApplicable = complianceDefaultValues["esiApplicable"];
            if(isEsiApplicable) {
                field.isMandatory = true;
                field.tooltip = "ESI Number should be of 10 to 17 digits";
            } else {
                field.isMandatory = false;
                field.tooltip = "ESI Number should be of 10 to 17 digits";
            }
            break;
        case "socsoEmployerContributionOnly":
            const residentStatus1 = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["residenceStatus"], "id");
            const eisSocsoContribution = complianceDefaultValues["eisContribution"];
            if(residentStatus1  === "malaysian" || residentStatus1  === "permanentresident" || eisSocsoContribution) {
                complianceDefaultValues["socsoEmployerContributionOnly"] = false;
                field.readOnly = true;
            } else {
                field.readOnly = false;
            }
            break;
        case "epfNumber":
            const residentStatus3 = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["residenceStatus"], "id");
            let validators = field.validators || [];
            if(residentStatus3  === "malaysian" || residentStatus3  === "permanentresident") {
                let isPresent = validators.some( v => v === "required");
                if(!isPresent) {
                    validators.push("required");
                } 
                field.validators = validators;
            } else if(residentStatus3  === "foreigner" ) {
                validators = validators.filter(v => {return v != "required"});
                field.validators = validators;
            }
            break;
        case "eisContribution":
            const eisContribution = complianceDefaultValues["eisContribution"];
            if(eisContribution) {
                field.readOnly = true; 
            } else {
                field.readOnly = false; 
            }
            break;
        case  "assignCPFAccount":
          const immigrationValue = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["immigrationStatus"], "id");
          const selectedCPFAccount = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["assignCPFAccount"], "id");
          const clearSelection = {id: "clear_selection",name:"Clear Selection"};
          let cpfValidators = field.validators || [];
          if (immigrationValue === "foreignerEmploymentPass" || immigrationValue === "foreignerSPass" ) {
            cpfValidators = cpfValidators.filter(v => {return v != "required"});
            field.required = false
          } else {
            let isPresent = cpfValidators.some( v => v === "required");
            if(!isPresent) {
              cpfValidators.push("required");
              field.required = true
            } 
          }
          if(!Utility.isEmpty(selectedCPFAccount) && Array.isArray(field.options)) {
            let index = field.options.findIndex(f => f.id === 'clear_selection');
            if(index === -1) {
              field.options.splice(0, 0, clearSelection)
            } 
          } else if(Utility.isEmpty(selectedCPFAccount) && Array.isArray(field.options)) {
              let options = field.options.filter(f => f.id !== 'clear_selection');
              field.options = options;
          }
          field.validators = cpfValidators;
    }
  }

  const showAndHideFields = (defaultValues) => {
    let columnConfig = deepClone(complainceColumnConfig);
    let employeeMetaFields = deepClone(employeeMetaConfig);
    if (
      employeeMetaFields &&
      employeeMetaFields.sections &&
      employeeMetaFields.sections.length > 0
    ) {
      let totalVisibleFields = [];
      employeeMetaFields.sections.forEach((section, sectionIndex) => {
        let fields = section?.fields;
        let config = [];
        fields.forEach((field) => {
          updateAdditonalValues(field);
          if (
            field.conditionalHideShowField &&
            field.conditionalHideShowField.length > 0
          ) {
            field.conditionalHideShowField.forEach((showHidefield) => {
              let fieldIds = showHidefield.fieldId;
              let fieldValues = showHidefield.fieldValue;
              /**
               * OR condition for fieldIds and fieldValues in conditionalHideShowField
               */
              if (
                COMPLIANCE_FIELDS_WITH_OR_CONDITIONAL_FIELDS.includes(
                  field.fieldId
                )
              ) {
                for (let i = 0; i < fieldIds.length; i++) {
                  let item = fieldIds[i];
                  let isPresent = totalVisibleFields.some((x) => x.id === item);
                  const value = getValueFromDefaultValues(defaultValues[item],item, totalVisibleFields);
                  isPresent =
                    isPresent &&
                    fieldValues.some((x) =>
                      x.includes(value)
                    );
                  if (isPresent) {
                    field.hidden = false;
                    field.uiVisible = true;
                    let columnConfig = EmployeeListService.showHideFieldConfig(
                      field,
                      isInviteUserFlowEnabled,
                      getExternalOptions()
                    );
                    config.push(columnConfig);
                    totalVisibleFields.push(columnConfig);
                  } 
                //Commenting can used further
                //   else {
                //     defaultValues[field.fieldId] = field?.defaultValue ? field.defaultValue : CONSTANTS.EMPTY_STRING;
                //   }
                }
              } else {
                /**
                 * AND condition for fieldIds and fieldsValues in conditionalHideShowField
                 */
                let isPresent = true;
                for (let i = 0; i < fieldIds.length; i++) {
                  let item = fieldIds[i];
                  isPresent = totalVisibleFields.some((x) => x.id === item);
                  const value = getValueFromDefaultValues(defaultValues[item],item, totalVisibleFields);
                  isPresent =
                    isPresent &&
                    fieldValues.some((x) =>
                      x.includes(value)
                    );
                  if (!isPresent) {
                    break;
                  }
                }
                if (isPresent) {
                  field.hidden = false;
                  field.uiVisible = true;
                  let columnConfig = EmployeeListService.showHideFieldConfig(
                    field,
                    isInviteUserFlowEnabled,
                    getExternalOptions()
                  );
                  if (defaultValues[columnConfig.id]?.id === "contributionNotAtFullRate") {
                    columnConfig.options = [{ id: 'contributionNotAtFullRate', name: 'Contribution Not At Full Rate'}];
                  }
                  config.push(columnConfig);
                  totalVisibleFields.push(columnConfig);
                }
                //Commenting can used further
                //  else {
                //   defaultValues[field.fieldId] = field?.defaultValue ? field.defaultValue : CONSTANTS.EMPTY_STRING;
                // }
              }
            });
          } else {
            let columnConfig = EmployeeListService.showHideFieldConfig(
              field,
              isInviteUserFlowEnabled,
              getExternalOptions()
            );
            config.push(columnConfig);
            totalVisibleFields.push(columnConfig);
          }
        });
        columnConfig[sectionIndex].fields = [...config];
      });
      setComplianceConfig(columnConfig);
    }
  };

  const setValidation = (value, object) => {
    const validation = object.validators;
    let isInvalid = false;
    let validationMsg = "";
    for (let i = 0; i < validation.length; i++) {
      switch (validation[i]) {
        case "required":
          if (object.required && Utility.isEmpty(value)) {
            isInvalid = true;
            validationMsg = `Enter a valid ${object.name}`;
          } else {
            isInvalid = false;
            validationMsg = "";
          }
          break;
        case "minLength":
          let minLength = object?.minLength ? parseInt(object.minLength) : 0;
          if (minLength && value.length < minLength) {
            isInvalid = true;
            validationMsg = `${object.name} should be of minimum ${minLength} digits`;
          } else {
            isInvalid = false;
            validationMsg = "";
          }
          break;
        case "maxLength":
          let maxLength = object?.maxLength ? parseInt(object?.maxLength) : 0;
          if (maxLength && value.length > maxLength) {
            isInvalid = true;
            validationMsg = `${object.name} should be of maximum ${maxLength} digits`;
          } else {
            isInvalid = false;
            validationMsg = "";
          }
          break;
      }
      if (isInvalid) break;
    }
    return { isInvalid, validationMsg };
  };

  const hideShowColumnConfigForCompliance = (sections, defaultValues) => {
      if (employeeDetails && employeeDetails.dateOfJoining && sections && sections.length != -0) {
          let i9Index = sections.findIndex(s => s.title === COMPLIANCE_SECTION.ELIGIBILITY);
          let today = moment().format(FORMAT.DDMMYYYY_2)
          let doj = moment(employeeDetails.dateOfJoining).format(FORMAT.DDMMYYYY_2)
          let isNext = moment(moment(today, FORMAT.DDMMYYYY_2)).isBefore(moment(doj, FORMAT.DDMMYYYY_2))
          let isSame = moment(moment(today, FORMAT.DDMMYYYY_2)).isSame(moment(doj, FORMAT.DDMMYYYY_2))
          if (i9Index != -1 && (!isNext && !isSame)) {
              sections.splice(i9Index, 1);
              let employeeMetaDetails = employeeMetaConfig?.sections;
              let i9IndexFromMeta = employeeMetaDetails?.findIndex(
                  (s) => s.title === COMPLIANCE_SECTION.ELIGIBILITY
              );
              if (i9IndexFromMeta != -1) {
                  employeeMetaDetails.splice(i9IndexFromMeta, 1);
              }
          }
      }
      let totalVisibleFields = [];
      sections.forEach((section, sectionIndex) => {
          let fields = section?.fields;
          let config = [];
          fields.forEach((field) => {
              if (field.columnCode == "esiNumber") {
                  field.type = "number"
              }
              updateAdditonalValues(field);
              if (
                  field.conditionalHideShowField &&
                  field.conditionalHideShowField.length > 0
              ) {
                  field.conditionalHideShowField.forEach((showHidefield) => {
                      let fieldIds = showHidefield.fieldId;
                      let fieldValues = showHidefield.fieldValue;
                      /**
                       * OR condition for fieldIds and fieldValues in conditionalHideShowField
                       */
                      if (
                          COMPLIANCE_FIELDS_WITH_OR_CONDITIONAL_FIELDS.includes(
                              field.fieldId
                          )
                      ) {
                          for (let i = 0; i < fieldIds.length; i++) {
                              let item = fieldIds[i];
                              let isPresent = totalVisibleFields.some((x) => x.id === item);
                              const value = getValueFromDefaultValues(defaultValues[item], item, totalVisibleFields);
                              isPresent =
                                  isPresent &&
                                  fieldValues.some((x) =>
                                      x.includes(value)
                                  );
                              if (isPresent) {
                                  field.hidden = false;
                                  field.uiVisible = true;
                                  config.push(field);
                                  totalVisibleFields.push(field);
                              } else {
                                  defaultValues[field.fieldId] = "";
                              }
                          }
                      } else {
                          /**
                           * AND condition for fieldIds and fieldsValues in conditionalHideShowField
                           */
                          let isPresent = true;
                          for (let i = 0; i < fieldIds.length; i++) {
                              let item = fieldIds[i];
                              isPresent = totalVisibleFields.some((x) => x.id === item);
                              const value = getValueFromDefaultValues(defaultValues[item], item, totalVisibleFields);
                              isPresent =
                                  isPresent &&
                                  fieldValues.some((x) =>
                                      x.includes(value)
                                  );
                              if (!isPresent) {
                                  break;
                              }
                          }
                          if (isPresent) {
                              field.hidden = false;
                              field.uiVisible = true;
                              config.push(field);
                              totalVisibleFields.push(field);
                          } else {
                              defaultValues[field.fieldId] = "";
                          }
                      }
                  });
              } else {
                  config.push(field);
                  totalVisibleFields.push(field);
              }
          });
          sections[sectionIndex].fields = [...config];
      });
      return sections
  }

  const onCheck = (object) => {
    let columnConfig = deepClone(complainceColumnConfig);
    switch (object.id) {
      case "pf":
        complianceDefaultValues[object.id] = !complianceDefaultValues[object.id];
        if (complianceDefaultValues[object.id] && (
              (Utility.isEmpty(complianceDefaultValues["employeePFContributionRate"])) ||
              (Utility.isEmpty(complianceDefaultValues["employerPFContributionRate"]))
              )
          ) {
          const employeeContributionField =
            (employeeMetaConfig && employeeMetaConfig.sections && employeeMetaConfig.sections.length != 0)
              ? employeeMetaConfig.sections[0]?.fields?.find((field) => {
                    if(field.fieldId === "employeePFContributionRate") {
                      return field
                    }
                })
              : null;
          const employerContributionField =
          (employeeMetaConfig && employeeMetaConfig.sections && employeeMetaConfig.sections.length != 0)
              ? employeeMetaConfig.sections[0]?.fields?.find(
                  (field) => field.fieldId === "employerPFContributionRate"
                )
              : null;
          if (
            employeeContributionField &&
            employeeContributionField.options &&
            employeeContributionField.options.length != 0
          ) {
            complianceDefaultValues["employeePFContributionRate"] =
              employeeContributionField.options[0];
          }
          if (
            employerContributionField &&
            employerContributionField.options &&
            employerContributionField.options.length != 0
          ) {
            complianceDefaultValues["employerPFContributionRate"] =
              employerContributionField.options[0];
          }
        } else {
          complianceDefaultValues['employeePFContributionRate'] = {};
          complianceDefaultValues['employerPFContributionRate'] = {};
        }
            
          
        break;
      case "professionalTax":
        complianceDefaultValues[object.id] =
          !complianceDefaultValues[object.id];
        break;
      case "taxExemption":
        complianceDefaultValues[object.id] =
          !complianceDefaultValues[object.id];
        break;
        case "esiApplicable":
          complianceDefaultValues[object.id] =
            !complianceDefaultValues[object.id];
            validateEsiNumber(complianceDefaultValues[object.id], columnConfig);
          break;
        case "socsoEmployerContributionOnly":
            complianceDefaultValues[object.id] =
                !complianceDefaultValues[object.id];
            const residentStatus = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["residenceStatus"], "id");
            if(complianceDefaultValues[object.id] && residentStatus === "foreigner") {
                complianceDefaultValues["eisContribution"] = false;
            }
            break;
        case "eisContribution":
            complianceDefaultValues[object.id] =
                !complianceDefaultValues[object.id];
            const residentStatus1 = EmployeeListService.getSelectedIdFromObject(complianceDefaultValues["residenceStatus"], "id");
            if(complianceDefaultValues[object.id] && residentStatus1 === "foreigner") {
                complianceDefaultValues["socsoEmployerContributionOnly"] = false;
            }
            if(complianceDefaultValues[object.id]) {
                getFieldPopupMessage(object.id, complianceDefaultValues[object.id]);
            }
            break;
      default:
        complianceDefaultValues[object.id] =
          !complianceDefaultValues[object.id];
    }
    showAndHideFields(complianceDefaultValues);
    props.setUserDataUpdated(true);
  };

  const validateEsiNumber = (esiApplicable, configDetails) => {
    if(configDetails && configDetails[0]) {
      if (esiApplicable) {
        configDetails[0].fields.map((field) => {
          if (field.id === COLUMN_CODES.ESI_NUMBER) {
            field.required = true;
            field.hasError =
              complianceDefaultValues.esiNumber.length > field.maxLength ||
              complianceDefaultValues.esiNumber.length < field.minLength;
          }
        });
      } else {
        configDetails[0].fields.map((field) => {
          if (field.id === COLUMN_CODES.ESI_NUMBER) {
            field.required = false;
            field.hasError =
              (complianceDefaultValues.esiNumber.length > field.maxLength ||
                complianceDefaultValues.esiNumber.length < field.minLength) &&
              complianceDefaultValues.esiNumber.length != 0;
          }
        });
      }
    }
    
  };

  const updateSection = (sectionToUpdate) => {
    const customSectionsToUpdate = [...customSections];
    const idx = customSectionsToUpdate.findIndex((s) => s.name === sectionToUpdate.name);
    if (idx !== -1) {
      customSectionsToUpdate[idx] = sectionToUpdate;
    }

    const customSectionFieldValues = CustomSectionManager.getCustomSectionFields(customSectionsToUpdate);
    setCustomSectionFieldValues(customSectionFieldValues);
    setCustomSections(customSectionsToUpdate);
  }

  const onSectionExpandedState = (value, index) => {
    sectionExpandedState[index] = value;
    setSectionExpandedState(sectionExpandedState);
  };

  const loadCPFAccountDetails = async () => {
    try {
      const config = {
        Limit: 100,
        currentPage: 1,
        order: undefined,
        field: undefined,
        searchString: undefined,
      };
      EmployeeListService.apiConfig = config;
      await dispatch(fetchCPFAccount());
    } catch (err) {
      console.error("Error loading custom fields data: ", err);
    }
  };

  const getFunctionConfig = (fieldId) => {
    switch (fieldId) {
      case COLUMN_CODES.ASSIGN_CPF_ACCOUNT:
        addCPFAccount();
        break;
    }
  };

  const addCPFAccount = () => {
    enableCPFAccountPopup(true);
    // console.log("Add APF Account");
  };

  const onCPFPopupClose = (value) => {
    enableCPFAccountPopup(false);
    if(value) {
      loadCPFAccountDetails();
    }
  }

  const getFieldPopupMessage = (fieldId, value) => {
    switch (fieldId) {
        case "eisContribution":
            showAlert(
                "Enable EIS/SOCSO contribution ?",
                "Please note that once enabled, it cannot be disabled. Are you sure you want to enable it anyway ?",
                [
                    {
                        title: "Yes",
                        className: "bg-app text-white mr-r",
                        onClick: () => enableFieldValues(fieldId, value),
                    },
                    {
                        title: "No",
                        className: "bg-gray1 border-m",
                        onClick: () => revertFieldValues(fieldId, value),
                    }
                ]
            )
            break;
    
        default:
            break;
    }
  }

  const enableFieldValues = (fieldId, value) => {
    switch (fieldId) {
        case "eisContribution":
            complianceDefaultValues["eisContribution"] = value;
            break;    
        default:
            break;
    }
    showAndHideFields(complianceDefaultValues);
  }

  const revertFieldValues = (fieldId, value) => {
    switch (fieldId) {
        case "eisContribution":
            complianceDefaultValues["eisContribution"] = !value;
            break;
    
        default:
            break;
    }
    showAndHideFields(complianceDefaultValues);
  }

  const getComplainceFormFields = (section, sectionIndex) => {
    if (!TenantService.get(TENANT.IS_ADMIN)) {
        setReactRef(section?.fields, ERF.ADD_EMPLOYEE);
    }

    return (section?.fields?.map((obj, index) => {
        switch (obj.id) {
          case "panNumber":
          case "socialSecurityNumber":
          case "nric":
          case "fin":
          case "workPermitNumber":
          case "icNumber":
            return (
              <>
                {obj.uiVisible && (
                  <div className={`  web-width-50 ${obj.id} `} key={obj.id}>
                    <div
                      className="p-h-l mb-l"
                      style={{
                        width: `${"90%"}`,
                      }}
                    >
                      <MaskInput
                        setRef={(ref) => ERF.ADD_EMPLOYEE[obj.id] = ref}
                        showMask={mask}
                        title={obj.name}
                        direction={obj.direction}
                        value={complianceDefaultValues[obj.id]}
                        errorMessage={obj.errorMsg}
                        readOnly={
                           obj.readOnly 
                          //  || !hasPermission
                        }
                        required={obj.required}
                        tooltip={{
                          content: obj.tooltipText,
                          className: "warning-tooltip-prop",
                        }}
                        onChange={(value) => {
                          onChange(value, index, obj, obj.name, sectionIndex);
                        }}
                        canValidate={obj.hasError}
                        invalid={obj.hasError}
                      />
                    </div>
                  </div>
                )}
              </>
            );
          default:
            return (
              <>
                {(obj.type != INPUT_TYPE.CHECKBOX && obj.type !== INPUT_TYPE.RADIO_BUTTON) && obj.uiVisible && (
                  <div className={` web-width-50 ${obj.id} `} key={obj.id}>
                    <div
                      className="p-h-l mb-l"
                      style={{
                        width: `${"90%"}`,
                      }}
                    >
                      <DKInput
                        ref={ERF.ADD_EMPLOYEE[obj.id]}
                        autoFocus={index === 0 && props.autoFocus === true}
                        className={""}
                        icon={obj.icon}
                        title={obj.name}
                        placeholder={obj.name}
                        value={complianceDefaultValues[obj.id]}
                        formatter={(obj) => {
                          /** How to render a selected option in case of object **/
                          return obj.name;
                        }}
                        displayKey={"name"}
                        options={obj.options}
                        type={obj.type}
                        hasError={obj.hasError}
                        errorMsg={obj.errorMsg}
                        labelClassName=" text-align-left "
                        onChange={(value) => {
                          onChange(value, index, obj, obj.name, sectionIndex);
                        }}
                        direction={obj.direction}
                        required={obj.required}
                        isDisabled={!obj.editable}
                        dateFormat={props.dateFormat}
                        readOnly={
                          obj.readOnly 
                          // || !hasPermission
                        }
                        tooltip={{
                          content: obj.tooltipText,
                          className: "warning-tooltip-prop",
                        }}
                        onClick={() => {}}
                        dropdownConfig={{
                          className: "",
                          style: {},
                          allowSearch:
                            obj.options && obj.options.length > 6
                              ? true
                              : false,
                          searchableKey: "name",
                          data: obj.options,
                          renderer: (index, obj) => {
                            /** How to render value in Dropdown in case of object **/
                            return <DKLabel text={`${obj.name}`} />;
                          },
                          onSelect: (index, obj) => {
                            // onValueChange(obj, "Quarter");
                          },
                          multiSelect: obj.isMultiSelect,
                          button: !Utility.isEmpty(obj.buttonConfig)
                            ? {
                                title: obj.buttonConfig.title,
                                onClick: (e) => {
                                  getFunctionConfig(obj.id);
                                },
                                className: obj.buttonConfig.className,
                              }
                            : false,
                        }}
                        canValidate={obj.hasError}
                        validator={(value) => {
                          return !obj.hasError;
                        }}
                        errorMessage={obj.errMsg}
                      />
                    </div>
                  </div>
                )}
                {obj.type === INPUT_TYPE.CHECKBOX && obj.uiVisible && (
                  <div className="web-width-50 d-flex align-items-center">
                    <div
                      className="row p-h-l p-v-l"
                      style={{
                        pointerEvents: `${
                          ((!isAdmin 
                            // && !hasPermission
                            ) &&
                            DataGridUtility.checkIsFieldReadOnly(
                              obj.columnCode,
                              false
                            ) 
                          ) || obj.readOnly
                            ? "none"
                            : ""
                        }`,
                      }}
                      key={obj.id}
                    >
                      <div
                        onClick={() =>
                          onCheck(obj)
                        }
                      >
                        <DKCheckMark
                          isSelected={complianceDefaultValues[obj.id]}
                          color="bg-app"
                          disabled={obj.readOnly }
                        />
                      </div>
                      <DKLabel className="ml-s" text={obj.name} />
                      {obj.hasTooltip && obj.tooltipText != "" && (
                        <div style={{ pointerEvents: "all" }}>
                            <DKTooltipWrapper
                                content={obj.tooltipText}
                                tooltipClassName="warning-tooltip-prop warning-tooltip-prop "
                            >
                                <DKIcon
                                    src={ic_info_message}
                                    className="ml-s ic-s ml-r mt-xs cursor-hand "
                                    style={{ opacity: 0.4 }}
                                />
                            </DKTooltipWrapper>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {obj.type === INPUT_TYPE.RADIO_BUTTON && (
                  <div className={`web-width-50 d-flex align-items-center ${obj.id}`}>
                      <div className="p-h-l">
                          <div className="row">
                            <DKLabel className="" text={`${obj.name}${obj.required ? ` *` : ''}`  } />
                            {obj.hasError && (
                                <DKTooltipWrapper
                                content={`Select a valid ${obj.name}`}
                                tooltipClassName="warning-tooltip-prop warning-tooltip-prop "
                              >
                                <DKIcon
                                  src={ic_erro_info}
                                  className="ml-s ic-s ml-r mt-xs cursor-hand "
                                  
                                />
                              </DKTooltipWrapper>
                            )}
                          </div>
                        <div className="row p-v-l ">                      
                            {obj.radioButtons && getRadioButtons(obj)}
                        </div>
                      </div>
                  </div>
                )}
              </>
            );
        }
      })
    );
  };
  const getRadioButtons = (obj) => {
    return obj.radioButtons.map(radioField => {
      return (
        <div className="row" style={{ width: "auto" }}>
          <DKRadioButton
            isSelected={radioField.fieldId === complianceDefaultValues[obj.id]}
            color="bg-app"
            onClick={() => {
              // if(!hasPermission) {
              //   return
              // }
              complianceDefaultValues[obj.id] = radioField.fieldId
              let columnConfig = deepClone(complainceColumnConfig);
              columnConfig[obj.id] = radioField.fieldId;
              setComplianceConfig(columnConfig);
            }}
          />
          <DKLabel className="ml-s mr-l" text={radioField.fieldLabel} />
        </div>
      )
    })
  }

  const getComplainceSections = () => {
    return complainceColumnConfig.map((obj, index) => {
      return (
        <>
          <div className="mt-m parent-width bg-white border-radius-s card-shadow">
            <DKExpandableWrapper
              title={obj.title}
              // style={{ padding: "20px" }}
              className="collapsable"
              expanded={sectionExpandedState[index]}
              headerStyle={{ padding: "20px" }}
              onClick={(expanded) => {
                onSectionExpandedState(expanded, index);
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
                className={` parent-width  `}
              >
                {getComplainceFormFields(obj, index)}
              </div>
            </DKExpandableWrapper>
            {/* <DKLabel text={obj.title} className="fs-l mb-r pt-l p-h-l fw-m" /> */}
          </div>
        </>
      );
    });
  };

  return (
    <div className="d-flex row-responsive">
      <div className="web-width-70 mb-l">
        {complainceColumnConfig && getComplainceSections()}
        {Array.isArray(customSections) && (
            <Fragment>
              {EmployeeConfigService.getNonDefaultSections(customSections).map((section) => {
                return (
                  <div className="mt-xl parent-width bg-white border-radius-s card-shadow">
                    <DKLabel text={section.title} className="fw-m fs-l mb-m pt-l p-h-l" />
                    <DKLine className="bg-line-2" />
                    <div className="parent-width parent-width pb-m pt-s" style={{ display: "flex", flexWrap: "wrap" }}>
                      <EmployeeCustomSection
                        section={section}
                        updateSection={(sectionToUpdate) => {
                          updateSection(sectionToUpdate);
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </Fragment>
          )}
      </div>

      <div className="pt-l pl-l web-width-30 padding-empty-mobile padding-top-mobile">
        <RightPanel
          mask={mask}
          data={deepClone(employeeUpdatedInfo)}
          componentsList={employeeUpdatedInfo["components"]}
          benefitsList={employeeUpdatedInfo["benefits"]}
        />
      </div>
      {showCPFPopup && (
        <AddCpfAccount
          isEdit={false}
          onClose={(value) => onCPFPopupClose(value)}
          details={{}}
        />
      )}
    </div>
  );
});

export default ComplianceDetails;
